import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { interval, Subject, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, retry, switchMap, tap, throttle } from 'rxjs/operators';
import { County, ICounty } from '../../../foundation/models/enums/county.enum';
import { IJurisdiction, Jurisdiction } from '../../../foundation/models/enums/jurisdiction.enum';
import { CustomerCategoryType } from '../../models/enums/customer-category-type.enum';
import { CustomerIdentificationType } from '../../models/enums/customer-identification-type.enum';
import { OrganizationSearchCriteria } from '../../models/organization-search-criteria.model';
import { CustomerSearchService } from '../../shared/customer-search.service';
import { Panel } from 'primeng/panel';

const DEFAULT_ID_TYPE = 7;
const NATIVE_JURISDICTION = 4;

@Component({
    selector: 'organization-search',
    templateUrl: './organization-search.html'
})
export class OrganizationSearchComponent implements OnInit, OnDestroy {
    @Input() public data: any = {};
    @Input() public showResults = false;
    @Input() public showSearchCriteria = true;
    @Input() public searchInfo: any;
    @Output() public onAddCustomerClicked = new EventEmitter<OrganizationSearchCriteria>();
    @Output() public onCustomerSearching = new EventEmitter<boolean>();
    @Output() public onFormReset = new EventEmitter<any>();
    @Output() public onResultsReceived = new EventEmitter<any>();

    @ViewChild('toggleablePanel') toggleablePanel: Panel;

    public counties: ICounty[];
    public customerIdentificationTypes: any[];
    public identificationTypes = new CustomerIdentificationType();
    public searchForm: UntypedFormGroup;
    public showButtonLoading = false;
    public states: IJurisdiction[];
    public validationMessage = '';
    private searchOrganization$ = new Subject<void>();
    private searchOrganizationSubscription: Subscription;

    constructor(
        public formBuilder: UntypedFormBuilder,
        public service: CustomerSearchService
    ) {
    }

    public ngOnInit(): void {
        this.setupSearchOrganizationListener();
        this.initializeSearchForm();
        this.setupEnums();
        this.displayAnyResults();
        this.displayFirstValidationMessage();
    }

    ngAfterViewInit(): void {
        this.toggleablePanel.collapseIcon = 'pi pi-plus';
        this.toggleablePanel.expandIcon = 'pi pi-minus';
    }

    public ngOnDestroy(): void {
        this.searchOrganizationSubscription.unsubscribe();
    }

    public addNewCustomer(): void {
        let model = this.getModel();
        this.onAddCustomerClicked.emit(model);
    }

    public customerIdentificationType(): string {
        return this.getValues(this.searchForm.get('customerIdentificationTypeId').value, this.identificationTypes);
    }

    public reset(): void {
        this.ngOnInit();
        this.showResults = false;
        this.showSearchCriteria = true;
        this.validationMessage = '';
        this.onFormReset.emit(null);
    }

    public searchOrganization(): void {
        this.searchOrganization$.next();
    }

    public setupSearchOrganizationListener(): void {
        this.searchOrganizationSubscription = this.searchOrganization$
            .pipe(
                throttle(() => interval(500)),
                tap(() => {
                    this.onCustomerSearching.emit(true);
                    this.showResults = false;
                }),
                filter(() => this.searchForm.valid),
                tap(() => this.prepareViewPriorToSearch()),
                map(() => this.prepareCriteriaForSearch()),
                switchMap(preparedSearchCriteria => this.service.searchOrganizations(preparedSearchCriteria)),
                tap(() => this.setCriteriaExactSearch()),
                tap(response => this.prepareViewFollowingSearch(response)),
                catchError(error => {
                    this.setCriteriaExactSearch();
                    this.prepareViewFollowingError(error);
                    return throwError(error);
                }),
                retry()
            )
            .subscribe(
                response => {
                    this.data = response;
                    this.onResultsReceived.emit(this.data);
                }
            );
    }

    private displayFirstValidationMessage(): void {
        if (this.data && this.data.results) {
            if (this.data.results.length > 0 && this.data.results.every(r => r.validationResults && !!r.validationResults.length)) {
                this.validationMessage = this.data.results[0].validationResults[0].errorMessage;
            }
        }
    }

    private displayAnyResults(): void {
        if (this.data && this.data.results) {
            this.showResults = true;
            this.showSearchCriteria = false;
        }
    }

    private getValues = (id: number, types: any): string => {
        if (id !== undefined && id !== null && id !== -1) {
            return types.FromId(id).displayValue;
        } else {
            return '';
        }
    };

    private initializeSearchForm(): void {
        this.searchForm = this.formBuilder.group({
            exactSearch: [this.searchInfo && this.searchInfo.exactSearch ? this.searchInfo.exactSearch : true],
            customerIdentificationTypeId: [this.searchInfo && this.searchInfo.customerIdentificationTypeId ? this.searchInfo.customerIdentificationTypeId : DEFAULT_ID_TYPE],
            customerIdentificationValue: [this.searchInfo && this.searchInfo.customerIdentificationValue ? this.searchInfo.customerIdentificationValue : null, Validators.maxLength(50)],
            organizationName: [this.searchInfo && this.searchInfo.organizationName ? this.searchInfo.organizationName : '', Validators.maxLength(256)],
            addressLine1: [this.searchInfo && this.searchInfo.addressLine1 ? this.searchInfo.addressLine1 : '', Validators.maxLength(40)],
            addressLine2: [this.searchInfo && this.searchInfo.addressLine2 ? this.searchInfo.addressLine2 : '', Validators.maxLength(40)],
            postalZipCode: [this.searchInfo && this.searchInfo.postalZipCode ? this.searchInfo.postalZipCode : '', Validators.maxLength(9)],
            city: [this.searchInfo && this.searchInfo.city ? this.searchInfo.city : '', Validators.maxLength(20)],
            jurisdictionId: [this.searchInfo && this.searchInfo.jurisdictionId ? this.searchInfo.jurisdictionId : null],
            countyId: [this.searchInfo && this.searchInfo.countyId ? this.searchInfo.countyId : null],
            phoneNumber: [this.searchInfo && this.searchInfo.phoneNumber ? this.searchInfo.phoneNumber : '', Validators.maxLength(100)],
            emailAddress: [this.searchInfo && this.searchInfo.emailAddress ? this.searchInfo.emailAddress : '', Validators.maxLength(100)]
        }, {
            validator: Validators.compose([
                this.organizationNameOrIdNumberRequiredValidation,
                this.idTypeValidator,
                this.stateRequiredValidator,
                this.countyWrongStateValidator(NATIVE_JURISDICTION)
            ])
        });

        if (this.searchInfo) {
            // if form was populated with searchInfo, enable "Find" button (if it is valid) by marking form as dirty and running validators
            this.searchForm.updateValueAndValidity();
            this.searchForm.markAsDirty();
        }
    }

    private prepareCriteriaForSearch(): OrganizationSearchCriteria {
        let model = this.getModel();
        let searchCriteria = Object.assign(new OrganizationSearchCriteria(), model);

        if (!searchCriteria.customerIdentificationValue) {
            searchCriteria.customerIdentificationTypeId = undefined;
        } else {
            searchCriteria.exactSearch = true;
        }

        return searchCriteria;
    }

    private setCriteriaExactSearch() {
        if (!!this.searchForm.get('customerIdentificationValue').value) {
            this.searchForm.get('exactSearch').setValue(false);
        }
    }

    private prepareViewFollowingError(error: any): void {
        this.validationMessage = error.response && error.response.validationResults ? error.response.validationResults[0].errorMessage : '';
        this.showButtonLoading = false;
    }

    private prepareViewFollowingSearch(response: any): void {
        this.showButtonLoading = false;
        this.showResults = true;
        let resultTotalCount = response.results ? response.results.length : 0;
        this.showSearchCriteria = (resultTotalCount === 0) || (resultTotalCount > 250);
    }

    private prepareViewPriorToSearch(): void {
        this.showButtonLoading = true;
        this.showResults = false;
        this.validationMessage = '';
    }

    private setupEnums(): void {
        this.customerIdentificationTypes = this.identificationTypes.GetActive().filter(it => it.customerCategoryType == (new CustomerCategoryType).Organization.id || it.customerCategoryType == (new CustomerCategoryType).Both.id);
        this.states = new Jurisdiction().GetActive();
        this.counties = new County().GetActive();
    }

    private idTypeValidator(group: any): { [key: string]: any } | null {
        let customerIdentificationValue = group.get('customerIdentificationValue').value;
        let customerIdentificationTypeId = group.get('customerIdentificationTypeId').value;

        if (((customerIdentificationValue != null) && (customerIdentificationValue.length > 0)) &&
            ((customerIdentificationTypeId == null) || (customerIdentificationTypeId == 0))
        ) {
            return { 'idTypeRequired': true };
        }
    }

    private organizationNameOrIdNumberRequiredValidation(group: any): { [key: string]: any } | null {
        let organizationName = group.get('organizationName').value;
        let customerIdentificationValue = group.get('customerIdentificationValue').value;

        if (((organizationName == null) || (organizationName.length == 0)) &&
            ((customerIdentificationValue == null) || (customerIdentificationValue.length == 0))
        ) {
            return { 'organizationNameOrIdNumberRequired': true };
        }
    }

    private stateRequiredValidator(group: any): { [key: string]: any } | null {
        let city = group.get('city').value;
        let jurisdictionId = group.get('jurisdictionId').value;

        if (((city != null) && (city.length > 0)) &&
            ((jurisdictionId == null) || (jurisdictionId == 0))
        ) {
            return { 'stateRequired': true };
        }
    }

    private countyWrongStateValidator(nativeJurisdiction: number): (FormGroup) => { [key: string]: any } | null {
        return (group: UntypedFormGroup): { [key: string]: any } => {
            let countyId = group.get('countyId').value;
            let jurisdictionId = group.get('jurisdictionId').value || 0;

            if (countyId &&
                (jurisdictionId != nativeJurisdiction)) {
                return { 'countyWrongState': true };
            }
        };
    }

    private getModel = (): OrganizationSearchCriteria => {
        let model = new OrganizationSearchCriteria();
        model.exactSearch = this.searchForm.get('exactSearch').value;
        model.customerIdentificationTypeId = this.searchForm.get('customerIdentificationTypeId').value;
        model.customerIdentificationValue = this.searchForm.get('customerIdentificationValue').value;
        model.organizationName = this.searchForm.get('organizationName').value;
        model.addressLine1 = this.searchForm.get('addressLine1').value;
        model.addressLine2 = this.searchForm.get('addressLine2').value;
        model.postalZipCode = this.searchForm.get('postalZipCode').value;
        model.city = this.searchForm.get('city').value;
        model.jurisdictionId = this.searchForm.get('jurisdictionId').value;
        model.countyId = this.searchForm.get('countyId').value;
        model.phoneNumber = this.searchForm.get('phoneNumber').value;
        model.emailAddress = this.searchForm.get('emailAddress').value;

        return model;
    };
}
